import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Navigate } from "react-router-dom";
import {
  Button,
  ConfigProvider,
  message,
  Modal,
  Pagination,
  Table,
  Tag,
  Tooltip,
} from "antd";

import { MakeApiCall } from "../../../apis";
import { BorderFilter, primaryColor } from "../../../config";
import { TableLoading } from "../../../components/table-animation";
import { isSwitchedFromSuperAdminSession } from "../../../utils/auth";
import { Wrapper } from "../user-list/style";

const MAX_PREVIEW = 100;

const formatDateTime = (unix) => {
  const ts = parseInt(unix, 10);
  if (!ts) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  }).format(new Date(ts * 1000));
};

const TruncatedText = ({ text, max = MAX_PREVIEW }) => {
  const s = text == null || text === "" ? "—" : String(text).replace(/\r/g, "");
  if (s === "—") return <span className="text-muted">—</span>;
  if (s.length <= max) {
    return (
      <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{s}</span>
    );
  }
  const preview = `${s.slice(0, max)}…`;
  return (
    <Tooltip
      title={
        <pre
          style={{
            maxWidth: 560,
            maxHeight: 320,
            overflow: "auto",
            margin: 0,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontSize: 12,
          }}
        >
          {s}
        </pre>
      }
    >
      <span style={{ cursor: "help", wordBreak: "break-word" }}>{preview}</span>
    </Tooltip>
  );
};

const eventTypeColor = (type) => {
  const t = String(type || "").toLowerCase();
  if (t.includes("error")) return "error";
  if (t.includes("warn")) return "warning";
  if (t.includes("log") || t.includes("info")) return "processing";
  return "default";
};

export default function SystemLogs({ userData }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [detailRecord, setDetailRecord] = useState(null);

  const canAccessSystemLogs =
    userData?.loginType === "user" &&
    isSwitchedFromSuperAdminSession(userData);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    const url = `application-logs/user-system-logs?page=${page}&per-page=${pageSize}`;
    const response = await MakeApiCall(url, "get", null, true, {}, userData?.token);

    if (response?.status === true && response?.data) {
      setList(response.data.records || []);
      const p = response.data.pagination || {};
      setTotalCount(p.totalCount ?? 0);
      if (typeof p.page === "number") setPage(p.page);
      if (typeof p.page_size === "number") setPageSize(p.page_size);
    } else {
      setList([]);
      setTotalCount(0);
      if (response?.message) {
        message.warning(response.message);
      }
    }
    setLoading(false);
    setInitialLoad(false);
  }, [page, pageSize, userData?.token]);

  useEffect(() => {
    if (!canAccessSystemLogs) return;
    fetchLogs();
  }, [fetchLogs, canAccessSystemLogs]);

  if (!canAccessSystemLogs) {
    return <Navigate to="/dashboard" replace />;
  }

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 72,
    },
    {
      title: "Event name",
      dataIndex: "event_name",
      key: "event_name",
      width: 280,
      render: (text) => <TruncatedText text={text} max={120} />,
    },
    {
      title: "Type",
      dataIndex: "event_type",
      key: "event_type",
      width: 140,
      render: (text) => (
        <Tag color={eventTypeColor(text)}>{text || "—"}</Tag>
      ),
    },
    {
      title: "Code",
      dataIndex: "error_code",
      key: "error_code",
      width: 88,
      render: (text) => <span className="text-nowrap">{text ?? "—"}</span>,
    },
    {
      title: "Error message",
      dataIndex: "error_message",
      key: "error_message",
      width: 320,
      render: (text) => <TruncatedText text={text} max={100} />,
    },
    {
      title: "IP",
      dataIndex: "ip_address",
      key: "ip_address",
      width: 120,
      render: (text) => <TruncatedText text={text} max={40} />,
    },
    {
      title: "URL",
      dataIndex: "url",
      key: "url",
      width: 200,
      render: (text) => <TruncatedText text={text} max={60} />,
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      width: 168,
      render: (ts) => formatDateTime(ts),
    },
    {
      title: "",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => setDetailRecord(record)}>
          Details
        </Button>
      ),
    },
  ];

  const theme = {
    token: {
      colorPrimary: primaryColor,
    },
  };

  if (initialLoad && loading) {
    return (
      <Wrapper>
        <Helmet>
          <title>System Logs</title>
        </Helmet>
        <div
          className="card mb-3 mt-4 px-3"
          style={{ border: BorderFilter }}
        >
          <div className="card-header border-0 pt-6">
            <h3 className="card-title fw-bolder">System Logs</h3>
          </div>
        </div>
        <div className="card">
          <div style={{ padding: "0px 15px", marginTop: "10px" }}>
            <TableLoading
              id="system-logs-skeleton"
              columns={[1, 2, 3, 4, 5, 6, 7, 8]}
              checkBoxVal={false}
            />
          </div>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Helmet>
        <title>System Logs</title>
      </Helmet>

      <div className="row gy-5 g-xl-5">
        <div className="col-xxl-12">
          <div className="card card-xxl-stretch mb-5 mb-xl-8">
            <div className="card-header border-bottom-dashed">
              <h3 className="card-title fw-bolder">System Logs</h3>
            </div>
            <div className="card-body py-2">
              <div className="table-responsive">
                <Table
                  scroll={{ x: "max-content" }}
                  columns={columns}
                  loading={loading}
                  dataSource={list}
                  rowKey="id"
                  pagination={false}
                  components={{
                    table: (props) => (
                      <table
                        className="table table-row-dashed table-row-gray-300 align-middle gs-4 gy-4 fs-7"
                        {...props}
                      />
                    ),
                    header: {
                      wrapper: (props) => <thead {...props} />,
                      row: (props) => (
                        <tr {...props} className="fw-bolder text-dark bg-cus" />
                      ),
                      cell: (props) => <th {...props} />,
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row gy-5 g-xl-5">
        <div className="col-xxl-12">
          <div className="card card-xxl-stretch mb-5 mb-xl-8">
            <div className="card-body py-2">
              <ConfigProvider theme={theme}>
                <Pagination
                  className="mt-3"
                  current={page}
                  pageSize={pageSize}
                  total={totalCount}
                  showSizeChanger
                  pageSizeOptions={["10", "20", "50", "100"]}
                  onChange={(currentPage, newPageSize) => {
                    if (newPageSize !== pageSize) {
                      setPage(1);
                      setPageSize(newPageSize);
                    } else {
                      setPage(currentPage);
                    }
                  }}
                  showTotal={(total, range) =>
                    `${range[0]}-${range[1]} of ${total} records`
                  }
                />
              </ConfigProvider>
            </div>
          </div>
        </div>
      </div>

      <Modal
        title={
          detailRecord
            ? `Log #${detailRecord.id} — ${detailRecord.event_type || "Event"}`
            : "Log details"
        }
        open={!!detailRecord}
        onCancel={() => setDetailRecord(null)}
        footer={
          <Button type="primary" onClick={() => setDetailRecord(null)}>
            Close
          </Button>
        }
        width={720}
        style={{ top: 24 }}
      >
        {detailRecord && (
          <div className="fs-7" style={{ maxHeight: "70vh", overflow: "auto" }}>
            <p>
              <strong>Event name</strong>
            </p>
            <pre
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: 12,
                background: "#f5f5f5",
                padding: 12,
                borderRadius: 8,
              }}
            >
              {detailRecord.event_name || "—"}
            </pre>
            <p>
              <strong>Error message</strong>
            </p>
            <pre
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: 12,
                background: "#f5f5f5",
                padding: 12,
                borderRadius: 8,
              }}
            >
              {detailRecord.error_message || "—"}
            </pre>
            <p>
              <strong>Error data</strong>
            </p>
            <pre
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: 12,
                background: "#f5f5f5",
                padding: 12,
                borderRadius: 8,
              }}
            >
              {detailRecord.error_data || "—"}
            </pre>
            <p className="text-muted mb-0">
              IP: {detailRecord.ip_address || "—"} · URL: {detailRecord.url || "—"}{" "}
              · Created: {formatDateTime(detailRecord.created_at)}
            </p>
          </div>
        )}
      </Modal>
    </Wrapper>
  );
}
